import * as React from 'react';
import styled from 'styled-components';

export interface AnalyticsDimensionMetric {
    id: string;
    label: string;
    average: number;
    totalResponses: number;
}

interface DimensionMetricsProps {
    title: string;
    metrics: AnalyticsDimensionMetric[];
}

const BreakdownCard = styled.div`
    border: var(--border-thin) solid var(--appLigthGrey);
    border-radius: 8px;
    padding: var(--spacer-S);
    background: var(--bg-light);
    display: flex;
    flex-direction: column;
    gap: var(--spacer-XS);
`;

const BreakdownTitle = styled.div`
    font-size: 0.75rem;
    color: var(--text-muted);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: var(--spacer-XXS);
`;

const DimensionRow = styled.div`
    display: flex;
    align-items: center;
    gap: var(--spacer-XS);
`;

const DimensionLabel = styled.span`
    flex: 1;
    font-size: 0.875rem;
    color: var(--text-body);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const DimensionBarTrack = styled.div`
    flex: 0 0 80px;
    height: 6px;
    border-radius: 999px;
    background: var(--appLigthGrey);
    overflow: hidden;
`;

const DimensionBarFill = styled.div<{ $pct: number }>`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    border-radius: 999px;
    background: var(--brand-secondary);
    transition: width 0.5s ease;
`;

const DimensionValue = styled.span`
    flex: 0 0 2.8rem;
    text-align: right;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--brand-secondary);
`;

const DimensionCount = styled.span`
    flex: 0 0 2rem;
    text-align: right;
    font-size: 0.75rem;
    color: var(--text-muted);
`;

export const DimensionMetrics: React.FC<DimensionMetricsProps> = ({ title, metrics }) => {
    if (metrics.length === 0) return null;

    const maxAverage = Math.max(...metrics.map(metric => metric.average));

    return (
        <BreakdownCard>
            <BreakdownTitle>{title}</BreakdownTitle>
            {metrics.map(metric => (
                <DimensionRow key={`${title}-${metric.id}`}>
                    <DimensionLabel title={metric.label}>{metric.label}</DimensionLabel>
                    <DimensionBarTrack>
                        <DimensionBarFill $pct={maxAverage > 0 ? (metric.average / maxAverage) * 100 : 0} />
                    </DimensionBarTrack>
                    <DimensionValue>{metric.average.toFixed(1)}</DimensionValue>
                    <DimensionCount>({metric.totalResponses})</DimensionCount>
                </DimensionRow>
            ))}
        </BreakdownCard>
    );
};
